import { toaster } from "@/components/ui/toaster";
import { MobileUser } from "./users.schema";
import {
  getMobileUsers,
  GetMobileUsersProps,
  GetMobileUsersResponse,
  updateUserMobile,
} from "@/services/users.service";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getUMFilterRequest, UMFRProps } from "./filter-obj";
import { Dispatch, SetStateAction } from "react";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { AppRoutes } from "@/constants/routes";
import { NavigateFunction } from "react-router-dom";

type HandleValidateMobileUserParams = {
  item: MobileUser;
  setSelectedToValidate: (user: MobileUser | undefined) => void;
  setLoadingValidateUser: (value: boolean) => void;
  fetchMobileUsers: (filters?: any, showLoad?: boolean) => Promise<void>;
  currentFilters: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
};

export const handleValidateMobileUser = async ({
  item,
  setSelectedToValidate,
  setLoadingValidateUser,
  fetchMobileUsers,
  currentFilters,
  navigate,
  fetchPermissions,
}: HandleValidateMobileUserParams) => {
  try {
    setSelectedToValidate(item);
    setLoadingValidateUser(true);

    await updateUserMobile({ IdUsuario: Number(item?.id) });

    toaster.create({
      title: "Usuario mobile registrado exitosamente.",
      type: "success",
    });

    await fetchMobileUsers({ ...currentFilters }, false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.USERS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo registrar el usuario mobile.",
      });
    }
    throw error;
  } finally {
    setSelectedToValidate(undefined);
    setLoadingValidateUser(false);
  }
};

export interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  submitFilters: (event: FieldValues) => Promise<void>;
  setFiltersApplied: (val: boolean) => void;
  getValues: UseFormGetValues<FieldValues>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  submitFilters,
  getValues,
}: ClearFiltersHandlerProps) => {
  setValue("first_name", "");
  setValue("last_name", "");
  setValue("cuit", "");
  setValue("dni", "");
  setValue("phone", "");
  setValue("email", "");
  setFiltersApplied(false);

  submitFilters?.(getValues());
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchMobileUsers: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  fetchMobileUsers,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetMobileUsersProps> = getUMFilterRequest(
      event as UMFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchMobileUsers(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
    }
    throw error;
  }
};

interface FetchMobileUsersHandlerProps {
  setLoadingMobileUsers: Dispatch<SetStateAction<boolean>>;
  setMobileUsers: Dispatch<SetStateAction<GetMobileUsersResponse | null>>;
  filters?: GetMobileUsersProps;
  showLoad?: boolean;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchMobileUsersHandler = async ({
  setLoadingMobileUsers,
  setMobileUsers,
  filters,
  showLoad,
  navigate,
  fetchPermissions,
}: FetchMobileUsersHandlerProps) => {
  try {
    if (showLoad) setLoadingMobileUsers(true);

    const response = await getMobileUsers({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setMobileUsers(response);

    setLoadingMobileUsers(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de usuarios mobile.",
      });
    }
  } finally {
    setLoadingMobileUsers(false);
  }
};
